import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from '../../../../../../utils/AxiosWrapper';
import {
  ApiError,
  unwrapApiResponse,
} from '../../../../../../utils/unwrapApiResponse';
import { LinkedAccount } from '../../../../../../types/dbTypes';
import {
  DepositFundsAuthorization,
  DepositFundsRequest,
  Entity,
  LinkedAccountDepositEligibility,
  PendingTransactionItem,
} from '../../../../../../types/apiTypes';
import { DepositOutcome } from '../types/bankingTypes';
import { useUser } from '../../../../../../utils/UserContext';

// A linked account plus its (backend-computed) deposit-source eligibility — a FE composition of two
// generated types (the DB model + the list-response enrichment). Used by the deposit account picker.
export type DepositEligibleLinkedAccount = LinkedAccount &
  LinkedAccountDepositEligibility;

/**
 * The customer id the deposit hooks key their queries on. Exported so a caller can PREFETCH the
 * account list (e.g. warm the cache when the Transfer chooser opens) under the EXACT same cache key
 * the DepositModal uses — otherwise the prefetch lands under a different key and does nothing.
 */
export const useDepositCustomerId = (): string => {
  const { userId, userRecord } = useUser();
  return userRecord?.userType === Entity.Customer
    ? (userRecord?.customer_id ?? '')
    : (userId ?? '');
};

/**
 * Data layer for the "Deposit into TACH" feature (customer-initiated ACH debit pull), plus the
 * shared "Processing" band query which is direction-agnostic (deposits AND withdrawals):
 *   - useLinkedAccountsQuery      — source accounts, enriched with deposit-source eligibility (P0)
 *   - usePendingTransactionsQuery — in-flight deposits AND withdrawals for the "Processing" band
 *   - useDepositMutation          — submit a deposit; maps the backend contract to a DepositOutcome
 * The modal (P2) owns all UI/messaging; these hooks only fetch/mutate + refresh caches.
 */

/**
 * A fresh idempotency key per submit ATTEMPT. The backend keys the deposit reservation + workflow on
 * this, and by design a retry must use a NEW key (each attempt is its own reservation row), so the
 * modal calls this on every submit click. `crypto.randomUUID` is available in the app's secure
 * (https) context — no dependency needed.
 */
export const newDepositIdempotencyKey = (): string => crypto.randomUUID();

/** The customer's linked accounts, enriched (P0) with `deposit_eligible` / `deposit_ineligible_reason`. */
export const useLinkedAccountsQuery = (
  customerId: string | undefined,
  enabled: boolean
) => {
  const { callProtectedApi } = useApi();

  return useQuery<DepositEligibleLinkedAccount[]>({
    queryKey: ['linkedAccounts', customerId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/linkedAccounts/list/customer/${customerId ?? ''}`,
        'get'
      );
      return (
        unwrapApiResponse<{ linkedAccounts: DepositEligibleLinkedAccount[] }>(
          response,
          'Failed to load linked accounts'
        ).linkedAccounts ?? []
      );
    },
    enabled: !!customerId && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * In-flight (Pending) transactions — deposits AND withdrawals — for the "Processing" band above the
 * transactions feed. The endpoint is sign-agnostic (positive = deposit, negative = withdrawal), so a
 * single query feeds both directions.
 */
export const usePendingTransactionsQuery = (
  customerId: string | undefined,
  enabled: boolean
) => {
  const { callProtectedApi } = useApi();

  return useQuery<PendingTransactionItem[]>({
    queryKey: ['pendingTransactions', customerId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/pending/${customerId ?? ''}`,
        'get'
      );
      return (
        unwrapApiResponse<{ pending: PendingTransactionItem[] }>(
          response,
          'Failed to load pending transactions'
        ).pending ?? []
      );
    },
    enabled: !!customerId && enabled,
    // In-flight deposits change on settlement (minutes–days) — refresh on focus + a modest interval,
    // and it's invalidated immediately after a successful submit (below).
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

export interface DepositMutationVariables {
  linkedAccountId: string;
  internalBankAccountId: string;
  amountInCents: number;
  idempotencyKey: string;
  authorization: DepositFundsAuthorization;
}

/**
 * Submit a deposit. Returns a `DepositOutcome` for every EXPECTED result (success + business
 * failures the modal branches on) and only THROWS for unexpected failures (network / 400 / 500),
 * which reach React Query's error state + the global Sentry reporter.
 */
export const useDepositMutation = (customerId: string) => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation<DepositOutcome, unknown, DepositMutationVariables>({
    mutationKey: ['deposit', customerId],
    mutationFn: async (vars) => {
      const body: DepositFundsRequest = {
        customer_id: customerId,
        linked_account_id: vars.linkedAccountId,
        internal_bank_account_id: vars.internalBankAccountId,
        amount: vars.amountInCents, // cents
        idempotency_key: vars.idempotencyKey,
        authorization: vars.authorization,
      };

      // hideAlert=true (+ no errorMsg) → the modal owns ALL messaging; retries=0 → never auto-retry
      // a money movement. The wrapper resolves with the error response, which we map below.
      const response = await callProtectedApi<unknown>(
        '/linkedAccounts/deposit',
        'post',
        body,
        undefined,
        undefined,
        undefined,
        0,
        true
      );

      return mapDepositResponseToOutcome(response);
    },
    onSuccess: (outcome) => {
      // Only a real success moved anything — refresh the Processing band so the new pending deposit
      // shows immediately (the service writes the pending row synchronously before the workflow).
      if (outcome.status === 'success') {
        queryClient.invalidateQueries({
          queryKey: ['pendingTransactions', customerId],
        });
      }
    },
  });
};

/**
 * Maps the deposit endpoint's HTTP status + body to a `DepositOutcome`. Body shape is the backend's
 * `HttpResponse`: `{ data, message }`, so a structured error body (e.g. `{ reason }`) lives at
 * `response.data.data`. Expected business failures return an outcome; unexpected ones throw ApiError.
 */
export const mapDepositResponseToOutcome = (
  response: AxiosResponse | null
): DepositOutcome => {
  const status = response?.status;
  const message: string | undefined = response?.data?.message;
  const structured: { reason?: string } | undefined = response?.data?.data;

  // Network-level failure (no response at all) → unexpected.
  if (status === undefined) {
    throw new ApiError('Deposit request failed to reach the server');
  }

  // Any 2xx is a success — includes the 200 "already initiated" idempotent duplicate.
  if (status >= 200 && status < 300) {
    return { status: 'success' };
  }

  switch (status) {
    case 429:
      // A limit rejection carries a message; the transient resource lock does not.
      return message
        ? { status: 'limit_exceeded', message }
        : { status: 'resource_locked' };
    case 503:
      return { status: 'deposits_disabled', message };
    case 422:
      if (
        structured?.reason === 'not_linked' ||
        structured?.reason === 'debit_disabled' ||
        structured?.reason === 'no_auth_accounts'
      ) {
        return {
          status: 'account_not_debit_capable',
          reason: structured.reason,
          message,
        };
      }
      // 422 without a debit-capable reason = insufficient balance in the source account.
      return { status: 'insufficient_balance', message };
    case 409:
      if (structured?.reason === 'reauth_required') {
        return { status: 'reauth_required' };
      }
      throw new ApiError(message || 'Deposit conflict', { status });
    default:
      // 400 / 500 / anything else → unexpected; surface to the error state + Sentry.
      throw new ApiError(message || 'Deposit failed', {
        status,
        url: response?.config?.url,
      });
  }
};
